import Vue from 'vue';
import { retry } from './retry';

// Used until the catalogue is loaded, or when it cannot be loaded.
// Same labels as the API's `tmsName`.
const fallbackNames = {
  'MOTO': 'Moto',
  'PASSEIO': 'Veículo de passeio',
  'FIORINO': 'Utilitário pequeno',
  'VAN': 'Van',
  'HR': 'VUC Pequeno',
  'IVECO': 'VUC Médio',
  '3/4': 'VUC Grande',
  'TOCO': 'Toco',
  'TRUCK': 'Truck',
  'BITRUCK': 'Bi-Truck',
  'CARRETA': 'Carreta',
};

// Observable so that templates and computed properties re-render when the catalogue arrives.
const catalogue = Vue.observable({ labels: {} });

const isFilled = (value) => typeof value === 'string' && value.trim() !== '';

/**
 * Label of a vehicle: `tmsName` when not blank, else `name`, else the code.
 * Unknown or obsolete codes (e.g. ENCAIXE) are shown as they are.
 */
export function vehicleTypeLabel(code) {
  if (!code) {
    return '';
  }
  return catalogue.labels[code] ?? fallbackNames[code] ?? code;
}

/**
 * @param {Array<{code: string, name?: string, tmsName?: string|null}>} vehicles
 * @return {Object<string, string>} labels by code
 */
export function buildLabels(vehicles) {
  const labels = {};
  (Array.isArray(vehicles) ? vehicles : []).forEach((vehicle) => {
    if (!vehicle || !isFilled(vehicle.code)) {
      return;
    }
    labels[vehicle.code] = [vehicle.tmsName, vehicle.name].find(isFilled) ?? vehicle.code;
  });
  return labels;
}

async function fetchCatalogue() {
  const response = await fetch('/jade/api/params/vehicle-types');
  if (!response.ok) {
    throw Object.assign(new Error(`GET /params/vehicle-types: HTTP ${response.status}`), { status: response.status });
  }
  return (await response.json()).vehicles;
}

/**
 * Loads the vehicle catalogue (with retries) and makes `vehicleTypeLabel` use it.
 * Never throws: on failure the fallback names keep being used.
 * @return {Promise<boolean>} whether the catalogue was loaded
 */
export async function loadVehicleTypes({ fetchVehicles = fetchCatalogue, retryOptions } = {}) {
  try {
    const labels = buildLabels(await retry(fetchVehicles, retryOptions));
    if (Object.keys(labels).length === 0) {
      return false;
    }
    catalogue.labels = labels;
    return true;
  } catch (error) {
    return false;
  }
}
